import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';

const STORAGE_KEY = 'onelive-sidebar-collapsed';

interface OneliveLayoutContextValue {
  sidebarCollapsed: boolean;
  toggleSidebar: () => void;
}

const OneliveLayoutContext = createContext<OneliveLayoutContextValue | null>(null);

export function OneliveLayoutProvider({ children }: { children: ReactNode }) {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) === '1';
    } catch {
      return false;
    }
  });

  const toggleSidebar = useCallback(() => {
    setSidebarCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(STORAGE_KEY, next ? '1' : '0');
      } catch {
        /* ignore */
      }
      return next;
    });
  }, []);

  const value = useMemo(
    () => ({ sidebarCollapsed, toggleSidebar }),
    [sidebarCollapsed, toggleSidebar],
  );

  return (
    <OneliveLayoutContext.Provider value={value}>
      {children}
    </OneliveLayoutContext.Provider>
  );
}

export function useOneliveLayout() {
  const ctx = useContext(OneliveLayoutContext);
  if (!ctx) {
    throw new Error('useOneliveLayout must be used within OneliveLayoutProvider');
  }
  return ctx;
}
